import ResponsiveContent from "@/elements/components/shared/responsive-content";
import { Tab } from "@/elements/components/ui/tab";
import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import React, { useCallback, useMemo, useState } from "react";
import { View, Text, Pressable } from "react-native";
import { api } from "@/elements/components/utils/convex-api";
import DownArrowIcon from "@/assets/svgs/down-arrow-icon";
import { SheetIds } from "@/elements/components/utils/sheet-ids";
import { useSheet } from "@/elements/components/shared/sheet-provider";
import ScheduleListActiveIcon from "@/assets/svgs/schedule-list-active-icon";
import {
  MultiSelectSheetPayload,
  MultiSelectSheetResult,
} from "../sheets/multi-select-sheet";
import { Id } from "@teachloop/backend/_generated/dataModel";
import StudentScheduleList from "./student-schedule-list";

function StudentSchedule() {
  const [statusFilter, setStatusFilter] = useState<
    "upcoming" | "requests" | "completed"
  >("upcoming");
  const [selectedSubjectIds, setSelectedSubjectIds] = useState<string[]>([]);
  const fromTime = useMemo(() => {
    const startOfDay = new Date();
    startOfDay.setHours(0, 0, 0, 0);
    return startOfDay.getTime();
  }, []);
  const { data: scheduleList } = useQuery({
    ...convexQuery(api.student.schedule.queries.getStudentSchedule, {
      statusFilter,
      fromTime,
      subjectIds: selectedSubjectIds as Id<"subjects">[],
    }),
  });
  const { items, count } = scheduleList ?? { items: [], count: 0 };
  const { data: subjects = [] } = useQuery(
    convexQuery(api.student.schedule.queries.getStudentSubjects, {})
  );
  const selectedSubjects = subjects.filter((subject) =>
    selectedSubjectIds?.includes(subject.id)
  );
  const { open } = useSheet<MultiSelectSheetPayload, MultiSelectSheetResult>(
    SheetIds.MULTI_SELECT
  );

  const handleSubjectSelect = useCallback(async () => {
    const result = await open({
      options: subjects.map((subject) => ({
        label: subject.name,
        value: subject.id,
      })),
      preSelectedOptions: selectedSubjectIds || [],
    });
    if (result.selectedOptions) {
      setSelectedSubjectIds(result.selectedOptions);
    }
  }, [subjects, open, selectedSubjectIds]);

  return (
    <View className="flex-1 bg-teachLoop-creamBase">
      <StudentScheduleList
        scheduleList={items}
        statusFilter={statusFilter}
        header={
          <ResponsiveContent>
            <View className="h-3" />
            <View className="mx-5">
              <View className="gap-1">
                <Text className="font-zilla-slab-medium font-medium text-dark text-[32px] leading-[1.2]">
                  Lesson schedule
                </Text>
                <Text className="font-geologica-regular font-normal text-[20px] text-gray-800 leading-[1.5]">
                  All your lessons in one place
                </Text>
              </View>
              <View className="h-8" />
              <View className="flex-row gap-2">
                <Tab
                  intent={statusFilter === "upcoming" ? "active" : "inActive"}
                  onPress={() => setStatusFilter("upcoming")}
                >
                  <Tab.Text>Upcoming</Tab.Text>
                </Tab>
                <Tab
                  intent={statusFilter === "requests" ? "active" : "inActive"}
                  onPress={() => setStatusFilter("requests")}
                >
                  <Tab.Text>Requests</Tab.Text>
                </Tab>
                <Tab
                  intent={statusFilter === "completed" ? "active" : "inActive"}
                  onPress={() => setStatusFilter("completed")}
                >
                  <Tab.Text>Completed</Tab.Text>
                </Tab>
              </View>
              <View className="h-6" />

              <View className="flex-row gap-6">
                <View className="flex-row items-center">
                  {selectedSubjects.length > 0 ? (
                    <Pressable
                      className="flex-row items-center gap-2 border border-gray-200 rounded-[15px] p-2"
                      onPress={handleSubjectSelect}
                    >
                      <Text className="font-geologica-medium text-gray-800 text-[16px] leading-[1.5]">
                        {selectedSubjects
                          .map((subject) => subject.name)
                          .join(", ") ?? ""}
                      </Text>
                      <DownArrowIcon pathClassName="fill-teachLoop-textPrimary" />
                    </Pressable>
                  ) : (
                    <Pressable
                      className="flex-row items-center gap-2 border border-gray-200 rounded-[15px] p-2"
                      onPress={handleSubjectSelect}
                    >
                      <Text className="font-geologica-medium text-gray-800 text-[16px] leading-[1.5]">
                        All subjects
                      </Text>
                      <DownArrowIcon pathClassName="fill-teachLoop-textPrimary" />
                    </Pressable>
                  )}
                </View>

                <View className="flex-row items-center gap-4">
                  <ScheduleListActiveIcon pathClassName="fill-teachLoop-textPrimary" />
                </View>
              </View>
            </View>

            <View className="h-6" />
            <View className="items-end mx-5">
              <View className="px-2 bg-teal-500 rounded-md">
                <Text className="font-geologica-medium font-medium text-xs text-white leading-[1.5] uppercase">
                  {count} Lessons in total
                </Text>
              </View>
            </View>
            <View className="h-2" />
            <View className="h-[1px] bg-gray-300" />
            <View className="h-6" />
          </ResponsiveContent>
        }
      />
    </View>
  );
}

export default StudentSchedule;
